import { memo, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { classes } from './ui.tsx';
import { currentNavPath, isCurrentPath } from './navigation-path.ts';
import { Icon, type IconName } from './icons.tsx';
import { useMediaQuery } from '../hooks/index.ts';
import type { LocationSummary, SessionUser } from '@shared/types/index.ts';

/**
 * The app's frame: a left sidebar from `sm` up (a labelled 256px sidebar from
 * `lg`, a 96px icon rail below it) and, on a phone, a bottom bar of four
 * destinations plus a "More" sheet holding the rest. The top bar, the page and
 * every notice between them are the layout's own.
 *
 * Session-agnostic on purpose: everything arrives as props, already filtered
 * and already translated. The office, the crew, the partner portal and the
 * operator console each have their own session — some of them their own
 * cookie and API — and the frame must not ask any of them for anything. Each
 * layout decides what its people can reach; this only draws it, the same way
 * for all of them. Hiding an entry is a courtesy: the server still refuses
 * what a role may not do.
 *
 * The groups follow one order everywhere: Home alone at the top, the labelled
 * groups in the order given, and the footer pinned at the bottom.
 */

/** One destination. `group` is `home`, `footer`, or a labelled group's id. */
export interface ShellNavItem {
  to: string;
  /** What the entry reads. */
  label: string;
  icon: IconName;
  group: string;
  /** Unread count, drawn as a badge. */
  count?: number;
  /** The accessible name while `count` is above zero, e.g. "Messages (3)". */
  countLabel?: string;
  /**
   * The hub page that lists this destination instead of the sidebar. The
   * sidebar leaves it out and, on its pages, marks the hub's entry current;
   * the phone's bottom bar and More sheet still offer it directly.
   */
  hubPath?: string;
}

export interface ShellNavGroup {
  id: string;
  label: string;
}

/**
 * `40rem` is Tailwind's `sm`, written in rem for the reason the help strip's
 * media query gives in index.css: a pixel cutoff drifts from Tailwind's own
 * breakpoint under a non-default browser font size. Below it the phone layout
 * (bottom bar and More sheet) applies; from it up, the sidebar does.
 *
 * Unless the window is also too short to list every destination at a size a
 * thumb can hit: a phone held sideways (an iPhone is 932x430 at most). There
 * the rail turns into the phone's bottom bar stood on end, the same four
 * destinations and "More", and the More sheet opens beside it. The `30rem`
 * height is matched by the `height <= 30rem` media queries in index.css.
 */
const SHORT_RAIL_QUERY = '(width >= 40rem) and (height <= 30rem)';

/** The full sidebar: wide enough for it, and tall enough to list everything. */
const FULL_SIDEBAR_QUERY = '(width >= 40rem) and (height > 30rem)';

/** How many destinations the phone's bottom bar holds beside "More". */
const PHONE_TAB_COUNT = 4;

const countOf = (item: ShellNavItem) => item.count ?? 0;

export function AppShell({
  items,
  groups,
  phoneTabOrder,
  brand,
  moreOpen,
  onMoreOpenChange,
  banner,
  topBar,
  topBarPhoneLine,
  notices,
  mainClassName = 'mx-auto max-w-6xl px-4 py-8',
  overlays,
  children,
}: {
  items: readonly ShellNavItem[];
  /** The labelled groups between Home and the footer, in sidebar order. */
  groups: readonly ShellNavGroup[];
  /**
   * Which destinations earn a place in the phone's bottom bar, best first.
   * The first four in `items` get a tab; everything else is under "More".
   */
  phoneTabOrder: readonly string[];
  /** Where the logo goes, and the name it carries for a screen reader. */
  brand: { to: string; label: string };
  moreOpen: boolean;
  onMoreOpenChange: (open: boolean) => void;
  /** Above everything, full width: the impersonation banner. */
  banner?: ReactNode;
  /** The top bar's controls, after the brand mark a phone shows. */
  topBar: ReactNode;
  /** A line of its own under the top bar, on a phone only. */
  topBarPhoneLine?: ReactNode;
  /** Notices between the top bar and the page. */
  notices?: ReactNode;
  mainClassName?: string;
  /** Dialogs the layout owns, mounted after the navigation. */
  overlays?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const shortRail = useMediaQuery(SHORT_RAIL_QUERY);

  // The phone's split: the first four reachable destinations in the tab order
  // sit in the bottom bar, and the rest go in the More sheet in sidebar order.
  // Both come off the one list, so whatever hid an item hides it from both.
  const [phoneTabs, sheetItems] = useMemo(() => {
    const tabs = phoneTabOrder.flatMap((to) => items.filter((item) => item.to === to)).slice(0, PHONE_TAB_COUNT);
    return [tabs, items.filter((item) => !tabs.includes(item))] as const;
  }, [items, phoneTabOrder]);
  const current = currentNavPath(
    location.pathname,
    items.map((item) => item.to),
  );
  // What the sidebar draws: everything but the destinations a hub page lists,
  // and on one of their pages, the hub's entry is the current one.
  const [sidebarItems, sidebarCurrent] = useMemo(() => {
    const hubbed = items.find((item) => item.to === current)?.hubPath;
    return [items.filter((item) => item.hubPath === undefined), hubbed ?? current] as const;
  }, [items, current]);

  // Closed on every navigation: a menu left open behind the page it just sent
  // someone to is a menu that looks broken the next time it's opened.
  const setMore = useRef(onMoreOpenChange);
  setMore.current = onMoreOpenChange;
  useEffect(() => {
    setMore.current(false);
  }, [location.pathname]);
  // Stable, so the memoized bottom bar is not redrawn by every render of the
  // layout around it.
  const openMore = useCallback(() => setMore.current(true), []);

  // The More sheet only exists for the phone layout and the short rail — its
  // wrapper is hidden under the full sidebar (index.css) — so crossing into
  // the full sidebar (widening a resizable window, a tablet's split view
  // growing taller) would hide a dialog that is `aria-modal` and still holds
  // focus, rather than closing it. Rotating a phone keeps it open: both ways
  // round it has a home. Listened for only while open, so this costs nothing
  // on desktop.
  useEffect(() => {
    if (!moreOpen) return;
    const query = window.matchMedia(FULL_SIDEBAR_QUERY);
    const onChange = () => {
      if (query.matches) setMore.current(false);
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [moreOpen]);

  // On a phone the page scrolls in `#main`, and that element stays mounted
  // across routes. The browser restores the window, which is not moving, so
  // each history entry keeps the offset it had and a new entry starts at the
  // top. A hash jump runs afterwards, in the page, and scrolls this element.
  // Replacing the query (picking a conversation) keeps the same history
  // index and must keep the offset; only a pushed entry starts at the top.
  const mainScroll = useRef(new Map<string, number>());
  const scrollSlot = useRef<{ key: string; index: number } | null>(null);
  useLayoutEffect(() => {
    const main = document.getElementById('main');
    if (!main) return;
    const key = location.key;
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    const prior = scrollSlot.current;
    if (prior && prior.index === index && prior.key !== key) {
      const kept = mainScroll.current.get(prior.key) ?? main.scrollTop;
      mainScroll.current.set(key, kept);
      main.scrollTop = kept;
    } else {
      main.scrollTop = mainScroll.current.get(key) ?? 0;
    }
    scrollSlot.current = { key, index };
    const remember = () => {
      mainScroll.current.set(key, main.scrollTop);
    };
    main.addEventListener('scroll', remember, { passive: true });
    return () => {
      mainScroll.current.set(key, main.scrollTop);
      main.removeEventListener('scroll', remember);
    };
  }, [location.key]);

  return (
    <div className="app-shell min-h-screen bg-[color:var(--color-surface)] sm:pl-24 lg:pl-64 print:pl-0">
      {/* Skip link so keyboard users can bypass the navigation (spec §27). */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:font-semibold"
      >
        {t('appLayout.skipToContent')}
      </a>

      {banner}

      {shortRail ? (
        <Sidebar
          items={phoneTabs}
          groups={[]}
          brandTo={brand.to}
          brandLabel={brand.label}
          current={current}
          more={{ items: sheetItems, open: moreOpen, onOpen: openMore }}
        />
      ) : (
        <Sidebar
          items={sidebarItems}
          groups={groups}
          brandTo={brand.to}
          brandLabel={brand.label}
          current={sidebarCurrent}
        />
      )}

      <header className="border-b border-[color:var(--color-line)] bg-white print:hidden">
        {/* Enlarged text can place the account controls on a second row.
            Keeping every control inside the viewport also keeps their
            disclosures inside the visible header. */}
        <div className="flex min-h-16 flex-wrap items-center gap-2 px-4 py-2 sm:gap-3 lg:px-6">
          {/* The sidebar carries the brand from `sm` up; on a phone it is
              hidden, so the mark leads the bar instead. */}
          <Link to={brand.to} className="shrink-0 rounded-md sm:hidden" aria-label={brand.label}>
            <img src="/BlastoClean-Icon.svg" alt="" aria-hidden="true" className="h-8 w-8" />
          </Link>
          {topBar}
        </div>
        {topBarPhoneLine ? <div className="px-4 pb-2 empty:hidden sm:hidden">{topBarPhoneLine}</div> : null}
      </header>

      {notices}

      {/* Focusable, so the skip link above moves the keyboard here too. */}
      <main id="main" tabIndex={-1} className={mainClassName}>
        {children}
      </main>

      <BottomBar tabs={phoneTabs} sheetItems={sheetItems} current={current} moreOpen={moreOpen} onOpenMore={openMore} />

      {moreOpen ? (
        <MoreSheet items={sheetItems} groups={groups} current={current} onClose={() => onMoreOpenChange(false)} />
      ) : null}

      {overlays}
      {/* In-flow room under everything else on a phone, the height of the
          fixed bottom bar (index.css, --shell-bottom-nav), so the end of the
          page stays above it. Zero tall wherever the bar does not show. */}
      <div className="h-[var(--shell-bottom-nav,0px)] shrink-0 print:hidden" aria-hidden="true" />
    </div>
  );
}

/** How many steps of spacing a navigation can shed (index.css, `data-density`). */
const DENSITIES = ['0', '1', '2', '3'] as const;

/**
 * Keeps a navigation from scrolling: when its roomy spacing does not fit,
 * it steps `data-density` on `frame` down until `scroller` holds every
 * destination. Measured rather than guessed from a height breakpoint,
 * because what fits depends on how many entries this person has, the
 * language and the text size. Written to the element directly, so fitting
 * costs no render. Only a window too short for the tightest spacing too — a
 * landscape phone, a browser zoomed far in — falls back to scrolling, rather
 * than cutting an entry off.
 */
function useFitDensity(
  frame: React.RefObject<HTMLElement | null>,
  scroller: React.RefObject<HTMLElement | null>,
  content: unknown,
) {
  useLayoutEffect(() => {
    const element = frame.current;
    const list = scroller.current;
    if (!element || !list) return;
    const fit = () => {
      for (const density of DENSITIES) {
        element.dataset.density = density;
        if (list.scrollHeight <= list.clientHeight) return;
      }
    };
    fit();
    window.addEventListener('resize', fit);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    observer?.observe(element);
    return () => {
      window.removeEventListener('resize', fit);
      observer?.disconnect();
    };
  }, [frame, scroller, content]);
}

/**
 * An unread count drawn beside (or, on an icon, over) a navigation entry.
 * Hidden from assistive technology: the entry's accessible name already says
 * the number, in the words `countLabel` gives it ("Messages (3)").
 */
function CountBadge({ count, floating, className }: { count: number; floating?: boolean; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={classes(
        'items-center justify-center rounded-full bg-[color:var(--color-brand)] font-bold leading-none text-white',
        floating
          ? 'absolute -top-1 -right-1 flex h-4 min-w-4 px-1 text-[10px]'
          : 'inline-flex h-5 min-w-5 shrink-0 px-1.5 text-[0.6875rem]',
        className,
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** A sidebar entry's shape: icon over its name on the rail, beside it from `lg`. */
const RAIL_ITEM_CLASSES =
  'sm:flex-col sm:gap-1 sm:px-1 sm:text-center sm:text-[0.6875rem] lg:flex-row lg:gap-3 lg:px-3 lg:text-left lg:text-[0.9375rem]';

/**
 * One destination in the sidebar, the tablet rail or the More sheet. The
 * current page is marked with `aria-current="page"` by NavLink, and the
 * `.nav-item` styles draw it: a tinted pill with an accent bar on its left
 * edge. A count, when there is one, is spoken as part of the link's name and
 * drawn as a badge.
 *
 * `end` unless this is the page's own destination, so a destination nested
 * under another (Visit coverage under Staff) is the only one marked current.
 * A hub's entry is current on the pages of the tools it lists, which are not
 * under its address, so there it is marked by hand.
 */
function SidebarLink({
  item,
  current,
  tiles,
  onClick,
}: {
  item: ShellNavItem;
  current: string | null;
  /** A tile in the More sheet's grid, whatever the width. */
  tiles?: boolean;
  onClick?: () => void;
}) {
  const unread = countOf(item);
  const { pathname } = useLocation();
  const className = classes('nav-item', tiles ? 'nav-tile' : RAIL_ITEM_CLASSES);
  const content = (
    <>
      <span className="relative flex-none">
        <Icon name={item.icon} size={22} />
        {unread > 0 ? <CountBadge count={unread} floating className={tiles ? undefined : 'lg:hidden'} /> : null}
      </span>
      {unread > 0 ? <span className="sr-only">{item.countLabel ?? item.label}</span> : null}
      <span
        aria-hidden={unread > 0 ? true : undefined}
        className={classes('min-w-0', tiles ? 'max-w-full break-words' : 'lg:flex-1')}
        data-nav-label=""
      >
        {item.label}
      </span>
      {/* Wrapped, so its own `inline-flex` cannot outrank the `hidden` that
          keeps it off the rail, where the badge floats on the icon instead. */}
      {unread > 0 && !tiles ? (
        <span className="hidden lg:contents">
          <CountBadge count={unread} />
        </span>
      ) : null}
    </>
  );
  if (current === item.to && !isCurrentPath(pathname, item.to)) {
    return (
      <Link to={item.to} onClick={onClick} className={className} aria-current="page">
        {content}
      </Link>
    );
  }
  return (
    <NavLink to={item.to} end={current !== item.to} onClick={onClick} className={className}>
      {content}
    </NavLink>
  );
}

function SidebarList({
  items,
  current,
  tiles,
  onNavigate,
  labelledBy,
}: {
  items: readonly ShellNavItem[];
  current: string | null;
  tiles?: boolean;
  onNavigate?: () => void;
  labelledBy?: string;
}) {
  if (items.length === 0) return null;
  return (
    <ul className={tiles ? 'grid grid-cols-3 gap-1' : 'space-y-1'} aria-labelledby={labelledBy}>
      {items.map((item) => (
        <li key={item.to}>
          <SidebarLink item={item} current={current} tiles={tiles} onClick={onNavigate} />
        </li>
      ))}
    </ul>
  );
}

/**
 * A labelled group of destinations. The label is a small muted heading in
 * the full sidebar and in the More sheet; the narrow rail has no room for it
 * and draws a divider instead. The list stays named by it either way, so a
 * screen reader hears "Sales, list, 3 items" at every width.
 */
function SidebarGroup({
  group,
  items,
  current,
  tiles,
  onNavigate,
}: {
  group: ShellNavGroup;
  items: readonly ShellNavItem[];
  current: string | null;
  tiles?: boolean;
  onNavigate?: () => void;
}) {
  const labelId = useId();
  if (items.length === 0) return null;
  return (
    <div className={tiles ? 'mt-2' : 'mt-2 lg:mt-4'} data-nav-group="">
      <p
        id={labelId}
        className={classes(
          'mb-1 px-3 text-xs font-semibold text-[color:var(--color-muted)]',
          tiles ? 'block' : 'hidden lg:block',
        )}
        data-nav-group-label=""
      >
        {group.label}
      </p>
      {tiles ? null : (
        <div
          className="mb-2 border-t border-[color:var(--color-line)] lg:hidden"
          aria-hidden="true"
          data-nav-divider=""
        />
      )}
      <SidebarList items={items} current={current} tiles={tiles} onNavigate={onNavigate} labelledBy={labelId} />
    </div>
  );
}

/**
 * The sidebar. Fixed to the left edge, so it stays put while the window
 * scrolls the page, and the shell's own left padding makes room for it —
 * which keeps `#main` a direct child of the shell, as the phone layout in
 * index.css needs. One element at every width: a 256px sidebar with labels
 * from `lg`, an icon rail from `sm`, and hidden below that, where the bottom
 * bar takes over. CSS decides, so a resize never remounts anything.
 */
const Sidebar = memo(function Sidebar({
  items,
  groups,
  brandTo,
  brandLabel,
  current,
  more,
}: {
  items: readonly ShellNavItem[];
  groups: readonly ShellNavGroup[];
  brandTo: string;
  brandLabel: string;
  current: string | null;
  /**
   * The short rail (SHORT_RAIL_QUERY): `items` are the phone's tabs, listed
   * as one, and "More" takes the footer's place, opening the sheet that
   * holds `more.items`.
   */
  more?: { items: readonly ShellNavItem[]; open: boolean; onOpen: () => void };
}) {
  const { t } = useTranslation();
  const nav = useRef<HTMLElement>(null);
  // The link a keyboard user is standing on, by its path. An entry can leave
  // after it was offered — Feedback is shown while the deployment switch is
  // still being asked, and the answer can be no — and removing the focused
  // link fires no blur: focus would fall to the document, past the reach of
  // the keyboard user's place in the list. Kept by path rather than node, so
  // the list it was in can be compared with the one replacing it.
  const focusedPath = useRef<string | null>(null);
  const previousItems = useRef(items);
  const aside = useRef<HTMLElement>(null);
  const list = useRef<HTMLDivElement>(null);
  useFitDensity(aside, list, items);
  useLayoutEffect(() => {
    const before = previousItems.current;
    previousItems.current = items;
    const lost = focusedPath.current;
    if (!lost || items.some((item) => item.to === lost)) return;
    focusedPath.current = null;
    if (document.activeElement && document.activeElement !== document.body) return;
    // The next entry that survived, or failing that the one before it.
    const index = before.findIndex((item) => item.to === lost);
    const survivor = [...before.slice(index + 1), ...before.slice(0, Math.max(0, index)).reverse()].find((item) =>
      items.some((next) => next.to === item.to),
    );
    if (survivor) nav.current?.querySelector<HTMLElement>(`a[href="${survivor.to}"]`)?.focus();
  }, [items]);
  return (
    <aside
      ref={aside}
      className="fixed inset-y-0 left-0 z-30 hidden w-24 flex-col border-r border-[color:var(--color-line)] bg-white sm:flex lg:w-64 print:hidden"
      data-shell-nav="sidebar"
    >
      <div className="flex h-16 shrink-0 items-center justify-center border-b border-[color:var(--color-line)] px-3 lg:justify-start lg:px-5">
        {/* The full lockup where there is room for it, the mark on the
            rail. The link carries the name once, whichever is showing. */}
        <Link to={brandTo} className="flex items-center rounded-md" aria-label={brandLabel}>
          <img src="/BlastoClean-Icon.svg" alt="" aria-hidden="true" className="h-8 w-8 shrink-0 lg:hidden" />
          <img src="/BlastoClean-Logo.svg" alt="" aria-hidden="true" className="hidden h-8 w-auto lg:block" />
        </Link>
      </div>
      <nav
        ref={nav}
        aria-label={t('nav.mainAriaLabel')}
        className="flex min-h-0 flex-1 flex-col"
        onFocus={(event) => {
          focusedPath.current = (event.target as HTMLElement).closest('a')?.getAttribute('href') ?? null;
        }}
        onBlur={(event) => {
          if (!(event.relatedTarget instanceof Node && nav.current?.contains(event.relatedTarget))) {
            focusedPath.current = null;
          }
        }}
      >
        <div ref={list} className="scrollbar-none min-h-0 flex-1 overflow-y-auto p-2 lg:p-3">
          {more ? (
            <SidebarList items={items} current={current} />
          ) : (
            <>
              <SidebarList items={items.filter((item) => item.group === 'home')} current={current} />
              {groups.map((group) => (
                <SidebarGroup
                  key={group.id}
                  group={group}
                  items={items.filter((item) => item.group === group.id)}
                  current={current}
                />
              ))}
            </>
          )}
        </div>
        <div className="shrink-0 border-t border-[color:var(--color-line)] p-2 lg:p-3">
          {more ? (
            <RailMoreButton items={more.items} current={current} open={more.open} onOpen={more.onOpen} />
          ) : (
            <SidebarList items={items.filter((item) => item.group === 'footer')} current={current} />
          )}
        </div>
      </nav>
    </aside>
  );
});

/**
 * What a "More" button says about the destinations folded behind it: whether
 * the current page is one of them, and their unread counts. The counts
 * survive the fold: with an item out of sight behind the button, the button's
 * label is what carries its number — in the text, so a screen reader
 * announces it too.
 */
function useMoreSummary(items: readonly ShellNavItem[], current: string | null) {
  const { t } = useTranslation();
  const unread = items.reduce((sum, item) => sum + countOf(item), 0);
  return {
    holdsCurrentPage: items.some((item) => item.to === current),
    unread,
    label: unread > 0 ? t('nav.moreWithUnread', { count: unread }) : t('nav.more'),
  };
}

/**
 * "More" on the short rail, drawn as one more rail entry. With the sheet
 * closed its links are unmounted, so on a page that lives in it the button
 * is what says where you are: `aria-current`, drawn as the current entry's
 * pill (index.css).
 */
function RailMoreButton({
  items,
  current,
  open,
  onOpen,
}: {
  items: readonly ShellNavItem[];
  current: string | null;
  open: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const summary = useMoreSummary(items, current);
  return (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-current={summary.holdsCurrentPage ? 'true' : undefined}
      onClick={onOpen}
      className={classes('nav-item w-full', RAIL_ITEM_CLASSES)}
      data-shell-more=""
    >
      {/* Padded on the rail as in the bottom bar: the dots are wider than
          they are tall, and a badge on their corner would hide the last. */}
      <span className="relative flex-none sm:px-2 lg:px-0">
        <Icon name="more" size={22} />
        {summary.unread > 0 ? <CountBadge count={summary.unread} floating className="lg:hidden" /> : null}
      </span>
      {summary.unread > 0 ? <span className="sr-only">{summary.label}</span> : null}
      <span aria-hidden={summary.unread > 0 ? true : undefined} className="min-w-0 lg:flex-1" data-nav-label="">
        {t('nav.more')}
      </span>
      {summary.unread > 0 ? (
        <span className="hidden lg:contents">
          <CountBadge count={summary.unread} />
        </span>
      ) : null}
    </button>
  );
}

/**
 * The phone's navigation: four destinations and "More", fixed to the bottom
 * of the screen within thumb reach. Hidden from `sm` up, where the sidebar
 * shows the same list.
 */
const BottomBar = memo(function BottomBar({
  tabs,
  sheetItems,
  current,
  moreOpen,
  onOpenMore,
}: {
  tabs: readonly ShellNavItem[];
  sheetItems: readonly ShellNavItem[];
  current: string | null;
  moreOpen: boolean;
  onOpenMore: () => void;
}) {
  const { t } = useTranslation();
  const {
    holdsCurrentPage: moreHoldsCurrentPage,
    unread: sheetUnread,
    label: moreLabel,
  } = useMoreSummary(sheetItems, current);
  return (
    <nav
      aria-label={t('nav.mainAriaLabel')}
      className="fixed inset-x-0 bottom-0 z-30 border-t border-[color:var(--color-line)] bg-white pb-[env(safe-area-inset-bottom)] sm:hidden print:hidden"
      data-shell-nav="bottom"
    >
      <ul className="flex">
        {tabs.map((item) => (
          <li key={item.to} className="min-w-0 flex-1">
            <BottomTab item={item} current={current} />
          </li>
        ))}
        <li className="min-w-0 flex-1">
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            // With the sheet closed its links are unmounted, so on a page
            // that lives in it the button is all the bar has to say where
            // you are — and the color alone says so only to eyes.
            aria-current={moreHoldsCurrentPage ? 'true' : undefined}
            onClick={onOpenMore}
            data-shell-more=""
            className={classes(
              'flex min-h-16 w-full flex-col items-center justify-center gap-0.5 px-1 text-[0.6875rem]',
              moreHoldsCurrentPage
                ? 'font-semibold text-[color:var(--color-brand-strong)]'
                : 'font-medium text-[color:var(--color-muted)]',
            )}
          >
            <span className="relative rounded-full px-3 py-0.5">
              <Icon name="more" size={22} />
              {sheetUnread > 0 ? <CountBadge count={sheetUnread} floating /> : null}
            </span>
            {sheetUnread > 0 ? <span className="sr-only">{moreLabel}</span> : null}
            <span aria-hidden={sheetUnread > 0 ? true : undefined} className="max-w-full truncate">
              {t('nav.more')}
            </span>
          </button>
        </li>
      </ul>
    </nav>
  );
});

/** A destination in the phone's bottom bar: icon over a short label. */
function BottomTab({ item, current }: { item: ShellNavItem; current: string | null }) {
  const unread = countOf(item);
  return (
    <NavLink
      to={item.to}
      end={current !== item.to}
      className={({ isActive }) =>
        classes(
          'flex min-h-16 flex-col items-center justify-center gap-0.5 px-1 text-center text-[0.6875rem]',
          isActive
            ? 'font-semibold text-[color:var(--color-brand-strong)]'
            : 'font-medium text-[color:var(--color-muted)]',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={classes('relative rounded-full px-3 py-0.5', isActive && 'bg-[color:var(--color-brand-soft)]')}
          >
            <Icon name={item.icon} size={22} />
            {unread > 0 ? <CountBadge count={unread} floating /> : null}
          </span>
          {unread > 0 ? <span className="sr-only">{item.countLabel ?? item.label}</span> : null}
          <span aria-hidden={unread > 0 ? true : undefined} className="max-w-full truncate">
            {item.label}
          </span>
        </>
      )}
    </NavLink>
  );
}

/**
 * The phone's "More" sheet: every destination the bottom bar has no room
 * for, grouped as the sidebar groups them, with the footer last. The short
 * rail opens the same sheet, as a panel beside it rather than from the
 * bottom edge (index.css).
 *
 * Modal-dialog behavior mirrors `ConfirmDialog`/`CommandPalette`: rendered
 * only while open, fixed above everything else, Escape and the backdrop
 * both close it, and focus goes to the first item on open and back to the
 * More button on close.
 *
 * Rendered in place rather than portalled to the body, so it stays inside
 * whatever the layout around the shell hides: the operator console conceals
 * and inerts its whole subtree while it re-checks who is signed in, and a
 * sheet outside that subtree would stay up, and usable, over the check.
 */
function MoreSheet({
  items,
  groups,
  current,
  onClose,
}: {
  items: readonly ShellNavItem[];
  groups: readonly ShellNavGroup[];
  current: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLElement>(null);
  useFitDensity(panel, list, items);

  // Focus goes back to the More button it came from, or, when the phone was
  // turned while the sheet was up, to the More button that shows now: the
  // rail's is unmounted in portrait, and the bottom bar's is hidden sideways.
  useEffect(() => {
    const opener = document.activeElement;
    panel.current?.querySelector<HTMLElement>('a')?.focus();
    return () => {
      const shown = (element: Element): element is HTMLElement =>
        element instanceof HTMLElement && element.isConnected && (element.checkVisibility?.() ?? true);
      const target =
        opener !== null && shown(opener) ? opener : [...document.querySelectorAll('[data-shell-more]')].find(shown);
      target?.focus();
    };
  }, []);

  // Traps Tab the same way ConfirmDialog does: the dialog is `aria-modal`, so
  // Tab cycling out of it into the backdrop-covered page behind would let a
  // keyboard user reach a control they cannot see.
  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...(panel.current?.querySelectorAll<HTMLElement>('a, button') ?? [])];
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  const footer = items.filter((item) => item.group === 'footer');
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[color:var(--color-ink)]/50 text-start"
      data-shell-sheet-backdrop=""
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-shell-sheet=""
        className="flex max-h-[90dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] shadow-2xl"
      >
        <div className="flex shrink-0 items-center justify-between px-5 pt-3 pb-1">
          <h2 id={titleId} className="text-lg font-semibold">
            {t('nav.more')}
          </h2>
          <button
            type="button"
            className="-mr-2 flex h-11 w-11 items-center justify-center rounded-md text-[color:var(--color-muted)] hover:bg-[color:var(--color-surface)]"
            onClick={onClose}
            aria-label={t('appLayout.closeMenu')}
          >
            <Icon name="close" />
          </button>
        </div>
        {/* A grid of tiles, three to a row, so every destination fits on a
            phone screen without scrolling. Only a viewport too short for
            any layout (a landscape phone, a zoomed browser) still scrolls,
            rather than clipping the last row: `min-h-0` lets the flex item
            shrink below its content so it scrolls instead. */}
        <nav ref={list} aria-label={t('nav.more')} className="scrollbar-none min-h-0 overflow-y-auto px-3 pb-3">
          <SidebarList
            items={items.filter((item) => item.group === 'home')}
            current={current}
            tiles
            onNavigate={onClose}
          />
          {groups.map((group) => (
            <SidebarGroup
              key={group.id}
              group={group}
              items={items.filter((item) => item.group === group.id)}
              current={current}
              tiles
              onNavigate={onClose}
            />
          ))}
          {footer.length > 0 ? (
            <div className="mt-2 border-t border-[color:var(--color-line)] pt-2">
              <SidebarList items={footer} current={current} tiles onNavigate={onClose} />
            </div>
          ) : null}
        </nav>
      </div>
    </div>
  );
}

/** Up to two initials for the account button, from the name or the email. */
function initialsOf(name: string): string {
  const letters = name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
  return letters || '?';
}

/**
 * The account button at the end of the top bar: who is signed in, and the
 * way out. A disclosure rather than an ARIA menu: `role="menu"` advertises
 * a keyboard contract — arrow keys, focus moving into the popup — that this
 * does not need, and it keeps the simpler one — the button
 * toggles, Tab walks the contents, Escape closes, and focus or a click
 * leaving it closes it.
 *
 * `settings` is a second way to the page the sidebar also pins, and like the
 * sidebar's it is offered to everyone: whatever a role may change about the
 * company, Settings is also where each person keeps their own profile, and
 * beside their name is where they look for it.
 *
 * "Sign out" still asks first (the confirmation lives in the layout). The
 * popup closes as it is chosen and hands focus back to the account button,
 * so the question has a control to return focus to when it is answered.
 */
export function AccountMenu({
  user,
  settings,
  signingOut,
  onSignOut,
  children,
}: {
  user: Pick<SessionUser, 'fullName' | 'email'>;
  settings: { to: string; label: string };
  signingOut: boolean;
  onSignOut: () => void;
  /** Personal preferences shown under the name, such as the language. */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  // Whether a primary press inside this control is still in flight — the one
  // moment a blur must not close the popup (see onBlur below).
  const pressInFlight = useRef(false);
  const name = user.fullName || user.email || '';

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  return (
    <div
      className="relative"
      ref={wrapper}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          setOpen(false);
          button.current?.focus();
        }
      }}
      onMouseDown={(event) => {
        // Only a primary press can end in a `click`; cleared on the release
        // wherever it lands, so a press dragged out cannot strand the flag.
        if (event.button !== 0) return;
        pressInFlight.current = true;
        window.addEventListener(
          'mouseup',
          () => {
            pressInFlight.current = false;
          },
          { once: true },
        );
      }}
      onBlur={(event) => {
        // Focus leaving closes the popup. One blur must survive: Safari gives
        // focus to nothing on a click, so choosing "Sign out" there blurs the
        // button with the click still in flight, and closing then would
        // unmount the very control being chosen.
        if (event.relatedTarget instanceof Node && wrapper.current?.contains(event.relatedTarget)) return;
        if (pressInFlight.current) return;
        setOpen(false);
      }}
    >
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={t('appLayout.accountMenu', { name })}
        onClick={() => setOpen(!open)}
        className="flex h-10 w-10 items-center justify-center rounded-full"
      >
        <span
          aria-hidden="true"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--color-brand-soft)] text-sm font-semibold text-[color:var(--color-brand-strong)]"
        >
          {initialsOf(name)}
        </span>
      </button>

      {open ? (
        <div
          id={panelId}
          className="absolute top-full right-0 z-30 mt-2 w-64 max-w-[calc(100vw-2rem)] rounded-md border border-[color:var(--color-line)] bg-white p-1 shadow-lg"
        >
          <div className="border-b border-[color:var(--color-line)] px-3 py-2">
            <p className="truncate text-sm font-semibold">{user.fullName}</p>
            {user.email ? <p className="truncate text-xs text-[color:var(--color-muted)]">{user.email}</p> : null}
          </div>
          {children ? <div className="border-b border-[color:var(--color-line)] px-3 py-2">{children}</div> : null}
          <Link
            to={settings.to}
            // Closed here as well as on the route change: from the settings
            // page itself the path does not change, and the popup would stay up.
            onClick={() => setOpen(false)}
            className="mt-1 flex min-h-11 w-full items-center gap-3 rounded px-3 text-left text-sm font-medium hover:bg-[color:var(--color-surface)]"
          >
            <Icon name="settings" className="shrink-0 text-[color:var(--color-muted)]" />
            {settings.label}
          </Link>
          <button
            type="button"
            disabled={signingOut}
            onClick={() => {
              setOpen(false);
              button.current?.focus();
              onSignOut();
            }}
            className="mt-1 flex min-h-11 w-full items-center gap-3 rounded px-3 text-left text-sm font-medium hover:bg-[color:var(--color-surface)] disabled:opacity-60"
          >
            <Icon name="signOut" className="shrink-0 text-[color:var(--color-muted)]" />
            {t('appLayout.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The header's location switcher. Rendered only for people assigned to two or
 * more active locations — everyone else has nothing to switch between and the
 * control would just be furniture. Switching goes through the session
 * provider, which refetches every cached query: all data on screen is scoped
 * to the working location, so a switch is a whole-app refresh by design.
 */
export function LocationSwitcher({
  currentLocation,
  locations,
  onSwitch,
}: {
  currentLocation: LocationSummary | null;
  locations: LocationSummary[];
  onSwitch: (locationId: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState(false);
  // Where the popup sits, measured as it opens and on every resize while
  // open: its left edge starts under the button and is clamped so the whole
  // panel stays a margin inside the viewport (spec §31.10). Hanging it from
  // one edge of the button or the other is not enough — on a phone the
  // switcher can sit mid-bar with room for the panel on neither side.
  const [panel, setPanel] = useState<{ left: number; width: number } | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = button.current?.getBoundingClientRect();
      if (!rect) return;
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const width = Math.min(16 * rem, window.innerWidth - 2 * rem);
      const left = Math.max(rem, Math.min(rect.left, window.innerWidth - rem - width));
      // Relative to the wrapper, which is where the absolute panel hangs from.
      setPanel({ left: left - rect.left, width });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  async function choose(location: LocationSummary) {
    if (location.id === currentLocation?.id) {
      setOpen(false);
      return;
    }
    setSwitching(location.id);
    setError(false);
    try {
      await onSwitch(location.id);
      setOpen(false);
    } catch {
      setError(true);
    } finally {
      setSwitching(null);
    }
  }

  return (
    <div className="relative" ref={wrapper}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('appLayout.locationSwitcherLabel')}
        ref={button}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
        }}
        className="flex min-h-10 max-w-56 shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium hover:bg-[color:var(--color-surface)] sm:border sm:border-[color:var(--color-line)] sm:px-3"
      >
        <Icon name="pin" size={18} className="shrink-0 text-[color:var(--color-muted)]" />
        {/* The name gives way on a phone, where the pin says what this is
            and the button's label says it to a screen reader. */}
        <span className="hidden truncate sm:inline">{currentLocation?.name ?? t('appLayout.locationNone')}</span>
        <Icon name="chevronDown" size={16} className="shrink-0" />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label={t('appLayout.locationSwitcherLabel')}
          style={panel ? { left: panel.left, width: panel.width } : undefined}
          className={classes(
            'absolute top-full z-30 mt-1 rounded-md border border-[color:var(--color-line)] bg-white p-1 shadow-lg',
            // Before the first measurement lands (one layout effect away).
            panel ? null : 'left-0 w-64 max-w-[calc(100vw-2rem)]',
          )}
        >
          {error ? (
            <p className="px-3 py-2 text-sm text-[color:var(--color-danger)]" role="alert">
              {t('appLayout.locationSwitchError')}
            </p>
          ) : null}
          {locations.map((location) => {
            const isCurrent = location.id === currentLocation?.id;
            return (
              <button
                key={location.id}
                type="button"
                role="menuitemradio"
                aria-checked={isCurrent}
                disabled={switching !== null}
                onClick={() => void choose(location)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setOpen(false);
                }}
                className={classes(
                  'flex w-full items-center justify-between gap-2 rounded px-3 py-2 text-left text-sm',
                  isCurrent ? 'font-semibold' : 'hover:bg-[color:var(--color-surface)]',
                )}
              >
                <span className="truncate">{location.name}</span>
                {switching === location.id ? (
                  <span
                    aria-hidden="true"
                    className="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
                  />
                ) : isCurrent ? (
                  <Icon name="check" size={16} className="shrink-0 text-[color:var(--color-brand)]" />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
